import { useState, useEffect, useRef, useCallback } from 'react'
import { getToken } from '@/stores/auth'
import { getEndpointLogs, type RequestLog } from '@/api/logs'
import { API_BASE_URL } from '@/lib/constants'

const MAX_LOGS = 200

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected'

interface UseEndpointLogsReturn {
  logs: RequestLog[]
  isLoading: boolean
  connectionStatus: ConnectionStatus
  loadMore: () => Promise<void>
  clearLogs: () => void
  hasMore: boolean
}

export function useEndpointLogs(endpointId: string, projectId: string): UseEndpointLogsReturn {
  const [logs, setLogs] = useState<RequestLog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting')
  const [hasMore, setHasMore] = useState(false)

  // Initial REST load filtered by endpoint
  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setLogs([])
    getEndpointLogs(endpointId, { limit: 50 })
      .then((data) => {
        if (cancelled) return
        setLogs(data)
        setHasMore(data.length === 50)
      })
      .catch(console.error)
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => { cancelled = true }
  }, [endpointId])

  // WebSocket: project-level stream filtered client-side by endpoint_id
  useEffect(() => {
    const token = getToken()
    if (!token) {
      setConnectionStatus('disconnected')
      return
    }

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsHost = API_BASE_URL ? new URL(API_BASE_URL).host : location.host
    const url = `${protocol}//${wsHost}/ws/projects/${projectId}/logs?token=${encodeURIComponent(token)}`

    setConnectionStatus('connecting')
    const ws = new WebSocket(url)

    ws.addEventListener('open', () => { setConnectionStatus('connected') })

    ws.addEventListener('message', (event) => {
      try {
        const log = JSON.parse(event.data as string) as RequestLog
        if (log.endpoint_id !== endpointId) return
        setLogs((prev) => {
          const next = [log, ...prev]
          return next.length > MAX_LOGS ? next.slice(0, MAX_LOGS) : next
        })
      } catch {
        // ignore malformed messages
      }
    })

    ws.addEventListener('close', () => { setConnectionStatus('disconnected') })
    ws.addEventListener('error', () => { setConnectionStatus('disconnected') })

    return () => { ws.close() }
  }, [endpointId, projectId])

  const logsRef = useRef(logs)
  logsRef.current = logs

  const loadMore = useCallback(async () => {
    const current = logsRef.current
    const oldest = current[current.length - 1]
    if (!oldest) return
    const data = await getEndpointLogs(endpointId, { limit: 50, before: oldest.created_at })
    setLogs((prev) => {
      const combined = [...prev, ...data]
      return combined.length > MAX_LOGS ? combined.slice(0, MAX_LOGS) : combined
    })
    setHasMore(data.length === 50)
  }, [endpointId])

  const clearLogs = useCallback(() => {
    setLogs([])
    setHasMore(false)
  }, [])

  return { logs, isLoading, connectionStatus, loadMore, clearLogs, hasMore }
}
